import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users2, Award, Car, Search, Sparkles, Plus } from "lucide-react";

const LABEL_COLOR = {
  "Strong Match": "text-gold-600 bg-gold-50 border-gold-200",
  "Good Match": "text-brand bg-brand-light border-brand",
  "Partial Match": "text-amber-700 bg-amber-50 border-amber-200",
  "Not Suitable": "text-red-700 bg-red-50 border-red-200",
};

export default function CompanyTeams() {
  const [teams, setTeams] = useState([]);
  const [q, setQ] = useState({ job_county: "" });

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (q.job_county) params.set("job_county", q.job_county);
      const { data } = await api.get(`/public/teams${params.toString() ? "?" + params.toString() : ""}`);
      setTeams(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load();  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-outfit font-bold text-white">Cluster Teams</h1>
        <p className="text-white/60 mt-1 font-manrope text-sm">Cluster Team rankings are always separate from individual guards and CPOs.</p>
      </div>

      <div className="card-flat p-5 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[180px]">
          <Label>Job county (drives team match score)</Label>
          <Input value={q.job_county} onChange={(e) => setQ({ ...q, job_county: e.target.value })} placeholder="e.g., Dublin" data-testid="teams-job-county" />
        </div>
        <Button onClick={load} className="bg-brand hover:bg-brand-hover text-white h-11 font-manrope font-semibold" data-testid="teams-search-btn">
          <Search className="w-4 h-4 mr-2" /> Score Teams
        </Button>
      </div>

      {teams.length === 0 && (
        <div className="card-flat p-8 text-center" data-testid="company-teams-empty">
          <Users2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="font-outfit font-semibold text-white mb-1">No cluster teams available for this search</div>
          <div className="text-white/60 font-manrope text-sm mb-5 max-w-md mx-auto">
            Either no lead pro has assembled a team in this area yet, or all teams are booked. You can build your own crew instead — post a job for a team of N and every matching professional gets notified.
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            <Link to="/company/jobs?team=1&workers=3&role=Security%20Guard">
              <Button className="bg-brand hover:bg-brand-hover text-white font-manrope font-semibold" data-testid="company-build-crew-btn">
                <Sparkles className="w-4 h-4 mr-1" /> Build your own crew — post a group job
              </Button>
            </Link>
            <Link to="/company/search">
              <Button variant="outline" data-testid="company-search-individuals-btn">
                <Search className="w-4 h-4 mr-1" /> Search individual professionals
              </Button>
            </Link>
          </div>
        </div>
      )}

      {teams.length > 0 && (
        <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm font-manrope text-blue-900 flex items-center gap-2" data-testid="company-teams-more-cta">
          <Sparkles className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <div className="flex-1">Need a bigger crew or specialist mix? Post a group job and hand-pick applicants.</div>
          <Link to="/company/jobs?team=1&workers=3&role=Security%20Guard">
            <Button size="sm" variant="outline" className="bg-[#0A0A0A]"><Plus className="w-4 h-4 mr-1" /> Post group job</Button>
          </Link>
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        {teams.map((t) => (
          <div key={t.id} className="card-flat p-6" data-testid={`team-view-${t.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <Users2 className="w-5 h-5 text-brand" />
              <div className="font-outfit font-semibold text-white text-lg">{t.name}</div>
            </div>
            <div className="text-sm text-white/60 font-manrope mb-2">Lead: <span className="ss-id">{t.lead_secusource_id}</span></div>
            <div className="grid grid-cols-3 gap-2 text-sm my-3">
              <div><div className="text-xs text-white/60 uppercase font-outfit">Members</div><div className="font-bold text-white">{t.num_members}</div></div>
              <div><div className="text-xs text-white/60 uppercase font-outfit">Experience</div><div className="font-bold text-white">{t.combined_experience} yr</div></div>
              <div className="flex items-center gap-1"><Car className={`w-4 h-4 ${t.vehicles ? "text-gold-500" : "text-slate-300"}`} /><span className="text-xs text-white/60">{t.vehicles ? "Vehicles" : "No vehicles"}</span></div>
            </div>
            {t.combined_kit?.length > 0 && (
              <div>
                <div className="text-xs uppercase text-brand font-outfit font-bold mb-1">Combined Kit</div>
                <div className="flex flex-wrap gap-1">{t.combined_kit.slice(0, 8).map((k) => <span key={k} className="chip"><Award className="w-3 h-3" />{k}</span>)}</div>
              </div>
            )}
            {t.match && (
              <div className={`mt-4 rounded-md border p-3 ${LABEL_COLOR[t.match.label] || LABEL_COLOR["Partial Match"]}`} data-testid={`team-match-${t.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest font-outfit font-bold">Team Match Score</div>
                  <div className="font-outfit font-bold text-white">{Math.round(t.match.score)}<span className="text-xs">%</span></div>
                </div>
                <div className="text-sm font-outfit font-semibold mt-0.5">{t.match.label}</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
